import { fintracStrAddressFields } from 'app/modules/fintracStr/formConfig/definitions/common/address';
import { fintracStrIdentificationWithJurisdictionFields } from 'app/modules/fintracStr/formConfig/definitions/common/identificationWithJurisdiction';
import { fintracStrPersonDetailsFields } from 'app/modules/fintracStr/formConfig/definitions/common/personDetails';
import { fintracStrTelephoneNumberFields } from 'app/modules/fintracStr/formConfig/definitions/common/telephoneNumber';
import { FINTRAC_STR_DEFINITIONS_ENTITIES_COMPLEX_FIELDS } from 'app/modules/fintracStr/formConfig/definitions/entities';
import { FINTRAC_STR_DEFINITIONS_PERSONS_COMPLEX_FIELDS } from 'app/modules/fintracStr/formConfig/definitions/persons';
import {
  FINTRAC_STR_DEFINITION_TYPE_CODE,
  FINTRAC_STR_DEFINITION_TYPE_CODE_OPTIONS,
} from 'app/modules/fintracStr/formConstants';
import { getDependentPaths } from 'app/modules/fintracStr/formUtils';
import {
  FintracFormFieldConditional,
  FintracFormSection,
  FintracFormValue,
} from 'app/modules/fintracStr/models';
import { U21FormFieldTypes } from 'app/shared/u21-ui/components';
import { v4 as uuidv4 } from 'uuid';

const isPersonOrEntityDetailsDefinition = (value: FintracFormValue) =>
  value === FINTRAC_STR_DEFINITION_TYPE_CODE.PERSON_DETAILS.value ||
  value ===
    FINTRAC_STR_DEFINITION_TYPE_CODE.PERSON_AND_EMPLOYER_DETAILS.value ||
  value === FINTRAC_STR_DEFINITION_TYPE_CODE.ENTITY_DETAILS.value ||
  value ===
    FINTRAC_STR_DEFINITION_TYPE_CODE.ENTITY_AND_BENEFICIAL_OWNERSHIP_DETAILS
      .value;

const isPersonOrEntityDetailsHideCondition: FintracFormFieldConditional = {
  or: [
    {
      path: (sectionIndex) => `definitions[${sectionIndex}].typeCode`,
      conditions: (value) => !isPersonOrEntityDetailsDefinition(value),
    },
  ],
};

const isPersonOrEntityDetailsOnlyDefinition = (value: FintracFormValue) =>
  value === FINTRAC_STR_DEFINITION_TYPE_CODE.PERSON_DETAILS.value ||
  value === FINTRAC_STR_DEFINITION_TYPE_CODE.ENTITY_DETAILS.value;

const isPersonOrEntityDetailsOnlyHideCondition: FintracFormFieldConditional = {
  or: [
    {
      path: (sectionIndex) => `definitions[${sectionIndex}].typeCode`,
      conditions: (value) => !isPersonOrEntityDetailsOnlyDefinition(value),
    },
  ],
};

const isPersonOrEntityDetailsComplexDefinition = (value: FintracFormValue) =>
  value ===
    FINTRAC_STR_DEFINITION_TYPE_CODE.PERSON_AND_EMPLOYER_DETAILS.value ||
  value ===
    FINTRAC_STR_DEFINITION_TYPE_CODE.ENTITY_AND_BENEFICIAL_OWNERSHIP_DETAILS
      .value;

const isPersonOrEntityDetailsComplexHideCondition: FintracFormFieldConditional =
  {
    or: [
      {
        path: (sectionIndex) => `definitions[${sectionIndex}].typeCode`,
        conditions: (value) => !isPersonOrEntityDetailsComplexDefinition(value),
      },
    ],
  };

export const FINTRAC_STR_DEFINITIONS_TAB_SECTION: FintracFormSection = {
  type: 'tabSection',
  repeatable: true,
  label: 'Subjects',
  name: 'definitions',
  mustExistInPayload: true,
  mustHaveValueIfExist: false,
  fields: [
    {
      type: U21FormFieldTypes.SELECT,
      name: 'typeCode',
      label: 'Subject type',
      required: true,
      fieldProps: {
        options: FINTRAC_STR_DEFINITION_TYPE_CODE_OPTIONS,
        clearable: false,
      },
      defaultValue: FINTRAC_STR_DEFINITION_TYPE_CODE.PERSON_DETAILS.value,
      mustExistInPayload: true,
      mustHaveValueIfExist: true,
    },
    {
      type: U21FormFieldTypes.TEXT,
      name: 'refId',
      label: 'Reference ID',
      mustExistInPayload: true,
      mustHaveValueIfExist: true,
      defaultValue: () => uuidv4(),
      disabled: true,
      help: 'This field is automatically generated and cannot be edited',
    },

    // Person basic fields
    ...fintracStrPersonDetailsFields({
      and: [
        {
          path: (sectionIndex) => `definitions[${sectionIndex}].typeCode`,
          conditions: (value) =>
            !(
              value === FINTRAC_STR_DEFINITION_TYPE_CODE.PERSON_NAME.value ||
              value === FINTRAC_STR_DEFINITION_TYPE_CODE.PERSON_DETAILS.value ||
              value ===
                FINTRAC_STR_DEFINITION_TYPE_CODE.PERSON_AND_EMPLOYER_DETAILS
                  .value
            ),
        },
      ],
    }),
    ...FINTRAC_STR_DEFINITIONS_PERSONS_COMPLEX_FIELDS,

    // Entity basic fields
    {
      type: U21FormFieldTypes.TEXT,
      name: 'nameOfEntity',
      label: 'Name of entity',
      mustExistInPayload: false,
      mustHaveValueIfExist: false,
      dependentPaths: (sectionIndex, fieldSectionIndex) =>
        getDependentPaths(
          undefined,
          [`definitions[${sectionIndex}].typeCode`],
          sectionIndex,
          fieldSectionIndex,
        ),
      hideIfTrue: {
        and: [
          {
            path: (sectionIndex) => `definitions[${sectionIndex}].typeCode`,
            conditions: (value) =>
              !(
                value === FINTRAC_STR_DEFINITION_TYPE_CODE.ENTITY_NAME.value ||
                value ===
                  FINTRAC_STR_DEFINITION_TYPE_CODE.ENTITY_DETAILS.value ||
                value ===
                  FINTRAC_STR_DEFINITION_TYPE_CODE
                    .ENTITY_AND_BENEFICIAL_OWNERSHIP_DETAILS.value
              ),
          },
        ],
      },
    },
    ...fintracStrTelephoneNumberFields(
      (sectionIndex) => `definitions[${sectionIndex}]`,
      isPersonOrEntityDetailsHideCondition,
    ),
    ...fintracStrAddressFields(
      (sectionIndex) => `definitions[${sectionIndex}]`,
      isPersonOrEntityDetailsHideCondition,
    ),

    ...FINTRAC_STR_DEFINITIONS_ENTITIES_COMPLEX_FIELDS,
    {
      type: 'section',
      repeatable: true,
      label: 'Identifications',
      name: 'identifications',
      mustExistInPayload: true,
      mustHaveValueIfExist: false,
      defaultNumOfEntries: 0,
      fields: fintracStrIdentificationWithJurisdictionFields(
        (sectionIndex, fieldSectionIndex) =>
          fieldSectionIndex && fieldSectionIndex.length > 0
            ? `definitions[${sectionIndex}].identifications[${fieldSectionIndex[0]}]`
            : '',
        isPersonOrEntityDetailsHideCondition,
      ),
      dependentPaths: (sectionIndex, fieldSectionIndex) =>
        getDependentPaths(
          [isPersonOrEntityDetailsHideCondition],
          undefined,
          sectionIndex,
          fieldSectionIndex,
        ),
      hideIfTrue: isPersonOrEntityDetailsHideCondition,
    },
    {
      type: 'section',
      repeatable: true,
      label: 'Beneficiary autofill fields',
      description:
        "Pre-fill these fields so they populate automatically when this subject is selected as a beneficiary in a transaction's completing actions.",
      repeatableButtonLabel: 'Beneficiary autofill fields',
      name: 'beneficiaryAutofilledFields',
      mustExistInPayload: false,
      mustHaveValueIfExist: false,
      maxRepeat: 1,
      defaultNumOfEntries: 0,
      fields: [
        [
          {
            type: U21FormFieldTypes.TEXT,
            name: 'clientNumber',
            label: 'Client number',
            required: false,
            mustExistInPayload: false,
            mustHaveValueIfExist: false,
          },
          {
            type: U21FormFieldTypes.TEXT,
            name: 'username',
            label: 'Username',
            required: false,
            mustExistInPayload: false,
            mustHaveValueIfExist: false,
          },
        ],
        {
          type: U21FormFieldTypes.TEXT,
          name: 'emailAddress',
          label: 'Email address',
          required: false,
          mustExistInPayload: false,
          mustHaveValueIfExist: false,
        },
      ],
      dependentPaths: (sectionIndex, fieldSectionIndex) =>
        getDependentPaths(
          [isPersonOrEntityDetailsOnlyHideCondition],
          undefined,
          sectionIndex,
          fieldSectionIndex,
        ),
      hideIfTrue: isPersonOrEntityDetailsOnlyHideCondition,
    },
    {
      type: 'section',
      repeatable: true,
      label: 'Conductor autofill fields',
      description:
        "Pre-fill these fields so they populate automatically when this subject is selected as a Conductor in a transaction's starting actions.",
      repeatableButtonLabel: 'Conductor autofill fields',
      name: 'conductorAutofilledFields',
      mustExistInPayload: false,
      mustHaveValueIfExist: false,
      maxRepeat: 1,
      defaultNumOfEntries: 0,
      fields: [
        [
          {
            type: U21FormFieldTypes.TEXT,
            name: 'clientNumber',
            label: 'Client number',
            required: false,
            mustExistInPayload: false,
            mustHaveValueIfExist: false,
          },
          {
            type: U21FormFieldTypes.TEXT,
            name: 'emailAddress',
            label: 'Email address',
            required: false,
            mustExistInPayload: false,
            mustHaveValueIfExist: false,
          },
        ],
        {
          type: U21FormFieldTypes.TEXT,
          name: 'url',
          label: 'URL',
          required: false,
          mustExistInPayload: false,
          mustHaveValueIfExist: false,
        },
      ],
      dependentPaths: (sectionIndex, fieldSectionIndex) =>
        getDependentPaths(
          [isPersonOrEntityDetailsComplexHideCondition],
          undefined,
          sectionIndex,
          fieldSectionIndex,
        ),
      hideIfTrue: isPersonOrEntityDetailsComplexHideCondition,
    },
  ],
};
